// convex/lib/valueMath.ts
//
// Pure dynasty-value math — no Convex runtime imports, mirroring the
// `scoringMath.ts` discipline. Consumed by the `'use node'` refresh action
// (`convex/rankings/fetch.ts`) and unit-tested directly in
// `src/__tests__/valueMath.test.ts`.
//
// Value model (player-value-rankings Phase 2):
//   value = wppg * contractFactor * ageFactor
// where wppg is a recency-weighted points-per-game over up to the last three
// seasons, contractFactor rewards years of team control, and ageFactor
// discounts positions past their historical decline knee.

/** Recency weights, latest season → oldest. Renormalized over the seasons
 *  actually present, so a single-season rookie gets weight 1.0 rather than
 *  being penalized for having no prior years.
 *  LOAD-BEARING LENGTH: the refresh action's season lookback is derived from
 *  this array's length (`LOOKBACK = SEASON_WEIGHTS.length` in
 *  convex/rankings/fetch.ts) — fetching fewer seasons than there are weights
 *  would silently under-weight history, more would fetch dead data. */
export const SEASON_WEIGHTS = [0.5, 0.3, 0.2];

/** Each remaining contract year adds 20% to value. contractYears 0 (unrostered
 *  or expiring) ⇒ factor 1.0. */
export const CONTRACT_FACTOR_PER_YEAR = 0.2;

/** Age at which decline starts, by position. Positions absent from this map
 *  (K, DEF, unknowns) get no age curve. */
export const AGE_KNEES: Record<string, number> = { QB: 33, RB: 27, WR: 29, TE: 29 };

/** Linear decay per year past the knee, floored at AGE_FACTOR_FLOOR. */
export const AGE_DECAY_PER_YEAR = 0.08;
export const AGE_FACTOR_FLOOR = 0.4;

export interface SeasonLine {
  year: number;
  totalPoints: number;
  weeksRostered: number;
  /** League-wide lineup record (BDL-VORP, 2026-08-27): weeks this player was
   *  in a starting lineup. Optional for backward compat; the graph's
   *  PlayerSeasonTotal always carries it, and absence is treated as 0. */
  weeksStarted?: number;
  /** Points scored in the weeks the player was actually started (graph
   *  rollup). Feeds the VORP lens's delivered rate (deliveredRate) —
   *  computeValue ignores it entirely. Missing ⇒ the rostered-week rate
   *  stands alone (back-compat). */
  startedPoints?: number;
}

export interface ValueInput {
  seasons: SeasonLine[];
  contractYears: number;
  age?: number;
  position: string;
}

export interface ValueBreakdown {
  wppg: number;
  contractFactor: number;
  ageFactor: number;
  value: number;
  seasons: Array<SeasonLine & { ppg: number }>;
}

function round(x: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(x * f) / f;
}

export function computeValue(input: ValueInput): ValueBreakdown {
  // ppg per season; weeksRostered=0 rows are dropped (no signal, avoids /0).
  const present = input.seasons
    .filter((s) => s.weeksRostered > 0)
    .sort((a, b) => b.year - a.year) // latest first, matching SEASON_WEIGHTS
    .slice(0, SEASON_WEIGHTS.length)
    .map((s) => ({ ...s, ppg: s.totalPoints / s.weeksRostered }));

  // Weights renormalized over PRESENT seasons: a rookie with one season gets
  // weight 1.0; two seasons get 0.5/0.8 and 0.3/0.8; etc.
  const weightSum = present.reduce((acc, _s, i) => acc + SEASON_WEIGHTS[i], 0);
  const wppg =
    weightSum === 0
      ? 0
      : present.reduce((acc, s, i) => acc + (SEASON_WEIGHTS[i] / weightSum) * s.ppg, 0);

  const contractFactor = 1 + CONTRACT_FACTOR_PER_YEAR * input.contractYears;

  const knee = AGE_KNEES[input.position];
  const ageFactor =
    input.age === undefined || knee === undefined
      ? 1
      : Math.max(AGE_FACTOR_FLOOR, 1 - AGE_DECAY_PER_YEAR * Math.max(0, input.age - knee));

  return {
    wppg: round(wppg, 3),
    contractFactor: round(contractFactor, 3),
    ageFactor: round(ageFactor, 3),
    value: round(wppg * contractFactor * ageFactor, 1),
    seasons: present.map((s) => ({
      year: s.year,
      totalPoints: s.totalPoints,
      weeksRostered: s.weeksRostered,
      ppg: round(s.ppg, 2),
    })),
  };
}

// -------- bones scale (2026-08-02, commissioner direction) --------
//
// The raw value is a great ORDER but an arbitrary unit. The league thinks in
// bones, so each refresh linearly rescales the cohort to auction-price range:
// the mean of the top TOP_ANCHOR_COHORT values maps to TOP_ANCHOR_BONES.
// Calibration: the 2025 RFA auction's peak winning bids were 86/90 (tagged
// stars), median contested bid 6 — anchoring the very top at 85 puts elite
// dynasty assets in the 70s-80s and the middle class in single digits,
// matching how the league actually pays. Tune TOP_ANCHOR_BONES to taste.

/** What the top of the board is "worth" in bones. */
export const TOP_ANCHOR_BONES = 85;
/** Anchor on the mean of the top-N values (not the single max) so one
 *  outlier season can't compress everyone else. */
export const TOP_ANCHOR_COHORT = 3;

/** Whole-bone auction-price scaling for the whole cohort. Pure and
 *  deterministic: same inputs ⇒ same bones. All-zero cohorts map to 0. */
export function assignBones(
  rows: Array<{ nflId: string; breakdown: ValueBreakdown }>,
): Map<string, number> {
  const topMean =
    [...rows]
      .sort((a, b) => b.breakdown.value - a.breakdown.value)
      .slice(0, TOP_ANCHOR_COHORT)
      .reduce((acc, r) => acc + r.breakdown.value, 0) /
    Math.min(TOP_ANCHOR_COHORT, Math.max(1, rows.length));
  const scale = topMean > 0 ? TOP_ANCHOR_BONES / topMean : 0;
  return new Map(rows.map((r) => [r.nflId, Math.max(0, Math.round(r.breakdown.value * scale))]));
}

// -------- BDL-VORP: the league's own supply/demand lens (2026-08-27) --------
//
// Commissioner-approved SECOND ranking lens, grounded in the league's OWN
// lineup record. It does NOT touch the market basis: the KTC-superflex
// basis, rank, and bones ship unchanged, and VORP rides alongside as a
// toggleable view (`vorp` / `vorpRank` / `vorpPosRank` on each row).
//
// COMPLETED SEASONS ONLY (commissioner direction, 2026-08-27): every VORP
// input — demand, replacement, production ppg, usage factor — computes from
// seasons strictly BEFORE the current league year. The in-progress season
// contributes NOTHING until it completes — partial-year starts would skew
// demand rates non-uniformly across positions. This also keeps
// WEEKS_PER_SEASON=17 a safe divisor. In-season blending is a future
// decision.
//
//   0. ELIGIBLE          = seasons with year < currentLeagueYear (the
//                          completed set; today 2025+2024 within LOOKBACK).
//   1. DEMAND     D_pos  = league-wide weekly starts at the position in the
//                          LATEST completed season with start signal ONLY:
//                          Σ weeksStarted / WEEKS_PER_SEASON. Latest-only
//                          because 2024 was pre-superflex — blending would
//                          drag the board halfway back to a 1QB world.
//   2. PRODUCTION wppg   = recency-weighted DELIVERED rate over the
//                          completed seasons present (SEASON_WEIGHTS,
//                          renormalized). Per season the rate is
//                          max(rostered-week ppg, per-start ppg when the
//                          start sample is big enough) — see deliveredRate:
//                          an injured star's rostered rate collapses while
//                          his per-start delivery stays true (revision
//                          2026-08-27 late, the Jayden Daniels case).
//                          P = wppg, RAW: "use" lives in DEMAND (starts
//                          define the replacement rank) — a per-player usage
//                          discount double-counted startability and crushed
//                          the post-format-change QB band (removed
//                          2026-08-27).
//   3. REPLACEMENT R_pos = the completed-season wppg of the player ranked
//                          ceil(D_pos) + REPLACEMENT_BUFFER within the
//                          position — the guy you could actually stream in.
//   4. VORP              = max(0, P − R_pos), in points/week surplus — the
//                          stored/displayed figure. The ORDERING (rankVorp)
//                          uses the unfloored, unrounded surplus so
//                          below-replacement players sort by their own
//                          positional shortfall.
//   5. LENS VALUE        = max(0, surplus) × contractFactor (the market
//                          lens's 20%/yr team-control multiplier —
//                          commissioner 2026-08-27): a 3-year elite surplus
//                          is worth more than an expiring one, but a long
//                          contract can never make a below-replacement
//                          player rank worse. Ranking + vorpBones use this;
//                          the displayed "/wk" figure stays the raw surplus.
//
// No age factor here — aging stays priced on the market lens.
//
// Empirical calibration (2024-25 bdl/history graph): QB demand jumped
// 10.6 → 20.6 starts/week when superflex began in 2025; RB ~32, WR ~31,
// TE ~11.5 have been stable — which is why demand never blends 2024 in.

/** Starts-total → starts/week divisor. ONLY safe on the completed-season
 *  path: the VORP lens excludes the in-progress season (see
 *  completedSeasons), so every season it divides is a full one. */
export const WEEKS_PER_SEASON = 17;

/** Demand uses ONLY the latest completed season with lineup data per
 *  position (not the SEASON_WEIGHTS blend): 2024 was pre-superflex (QB
 *  demand ~10.6/wk vs ~20.6 in 2025), and blending would drag the board
 *  halfway back to a 1QB world. Seasons whose position total is 0 starts
 *  are skipped, so "latest" means latest with signal. measureDemand takes
 *  this as its testable `latestOnly` default; false recency-weights across
 *  the lookback via SEASON_WEIGHTS instead. */
export const LATEST_SEASON_DEMAND = true;

/** Streaming slack: replacement level sits this many ranks below the last
 *  weekly starter (ceil(D_pos) + buffer), since the wire always has a few
 *  startable bodies beyond the mathematical cutoff. */
export const REPLACEMENT_BUFFER = 3;

/** Small-sample guard for the delivered rate: the per-start rate only counts
 *  when the season has at least this many starts. Below it, one boom week in
 *  a 1-3 start sample would fabricate an elite rate; at 4+ the per-start ppg
 *  is a real (if noisy) measurement of what the player delivers when the
 *  league trusts him with a lineup slot. */
export const MIN_STARTS_FOR_RATE = 4;

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/** Seasons eligible for the VORP lens: strictly before the current league
 *  year. Completed seasons only per the commissioner (2026-08-27) — the
 *  in-progress year's partial starts contribute nothing until it completes. */
export function completedSeasons(seasons: SeasonLine[], currentLeagueYear: number): SeasonLine[] {
  return seasons.filter((s) => s.year < currentLeagueYear);
}

/** The lens's vintage: the latest completed season with any start signal
 *  across the cohort (today: 2025). Undefined when no completed season has
 *  lineup data — the lens then has no signal at all. */
export function selectVorpSeason(
  rows: Array<{ seasons: SeasonLine[] }>,
  currentLeagueYear: number,
): number | undefined {
  let best: number | undefined;
  for (const row of rows) {
    for (const s of completedSeasons(row.seasons, currentLeagueYear)) {
      if ((s.weeksStarted ?? 0) > 0 && (best === undefined || s.year > best)) best = s.year;
    }
  }
  return best;
}

/** Measured demand per position: league-wide starts per week. Per season,
 *  D = sum(weeksStarted over players at the position) / WEEKS_PER_SEASON —
 *  callers must pass COMPLETED seasons only (see completedSeasons), which is
 *  what keeps the fixed divisor honest. Seasons with zero starts at the
 *  position carry no signal and are skipped. With latestOnly (default
 *  LATEST_SEASON_DEMAND) the latest such season wins outright; otherwise
 *  seasons blend under SEASON_WEIGHTS (renormalized over seasons present).
 *  Positions with no lineup data anywhere are absent from the map. */
export function measureDemand(
  rows: Array<{ position: string; seasons: SeasonLine[] }>,
  latestOnly: boolean = LATEST_SEASON_DEMAND,
): Map<string, number> {
  const startsByPosYear = new Map<string, Map<number, number>>();
  for (const row of rows) {
    for (const s of row.seasons) {
      const started = s.weeksStarted ?? 0;
      if (started <= 0) continue;
      const years = startsByPosYear.get(row.position) ?? new Map<number, number>();
      years.set(s.year, (years.get(s.year) ?? 0) + started);
      startsByPosYear.set(row.position, years);
    }
  }
  const out = new Map<string, number>();
  for (const [pos, years] of startsByPosYear) {
    const perSeason = [...years.entries()]
      .sort((a, b) => b[0] - a[0]) // latest first, matching SEASON_WEIGHTS
      .slice(0, SEASON_WEIGHTS.length)
      .map(([, starts]) => starts / WEEKS_PER_SEASON);
    if (latestOnly) {
      out.set(pos, round(perSeason[0], 2));
    } else {
      const weightSum = perSeason.reduce((acc, _d, i) => acc + SEASON_WEIGHTS[i], 0);
      out.set(
        pos,
        round(
          perSeason.reduce((acc, d, i) => acc + (SEASON_WEIGHTS[i] / weightSum) * d, 0),
          2,
        ),
      );
    }
  }
  return out;
}

/** Empirical replacement level: the wppg of the player ranked
 *  ceil(demand) + REPLACEMENT_BUFFER within the position (1-based, wppg
 *  desc). Shallower positions than the cutoff return the last player's wppg;
 *  empty positions return 0. */
export function replacementPpg(wppgs: number[], demand: number): number {
  if (wppgs.length === 0) return 0;
  const sorted = [...wppgs].sort((a, b) => b - a);
  const rank = Math.ceil(demand) + REPLACEMENT_BUFFER;
  return sorted[clamp(rank, 1, sorted.length) - 1];
}

/** Delivered rate for one season (revision 2026-08-27 late — the Jayden
 *  Daniels case: 2025 injury year, 120.4 pts over 18 ROSTERED weeks =
 *  6.69 ppg, but 100 pts over 6 STARTS = 16.7 — an elite starter when
 *  healthy, invisible at the rostered rate):
 *
 *    rate = max(totalPoints / weeksRostered,
 *               weeksStarted ≥ MIN_STARTS_FOR_RATE
 *                 ? startedPoints / weeksStarted : 0)
 *
 *  An injured star's rostered-week rate collapses while his per-start
 *  delivery stays true; the max() means a season can never look WORSE than
 *  what the player delivered when started, while bench stashes (few or no
 *  starts) keep the honest rostered-week rate. Missing startedPoints ⇒ the
 *  rostered rate stands alone (back-compat). */
export function deliveredRate(s: SeasonLine): number {
  const rostered = s.weeksRostered > 0 ? s.totalPoints / s.weeksRostered : 0;
  const starts = s.weeksStarted ?? 0;
  const started = starts >= MIN_STARTS_FOR_RATE ? (s.startedPoints ?? 0) / starts : 0;
  return Math.max(rostered, started);
}

/** Recency-weighted DELIVERED rate over the given lines — computeValue's
 *  weighting discipline (drop weeksRostered=0, latest-first, SEASON_WEIGHTS
 *  renormalized over seasons present) but UNROUNDED and over deliveredRate
 *  rather than the plain rostered-week ppg, kept separate so the VORP lens
 *  can weight the completed-season subset without touching the market path
 *  (computeValue is untouched — see the market-invariance regression test). */
export function weightedPpg(seasons: SeasonLine[]): number {
  const present = seasons
    .filter((s) => s.weeksRostered > 0)
    .sort((a, b) => b.year - a.year)
    .slice(0, SEASON_WEIGHTS.length)
    .map(deliveredRate);
  const weightSum = present.reduce((acc, _p, i) => acc + SEASON_WEIGHTS[i], 0);
  return weightSum === 0
    ? 0
    : present.reduce((acc, p, i) => acc + (SEASON_WEIGHTS[i] / weightSum) * p, 0);
}

export interface VorpInput {
  nflId: string;
  position: string;
  /** BDL contract years remaining — team control multiplies positive surplus
   *  into the lens value (commissioner 2026-08-27). */
  contractYears: number;
  /** RAW season lines (with weeksStarted), not the trimmed breakdown copy.
   *  computeVorpAll filters to completed seasons itself. */
  seasons: SeasonLine[];
}

export interface VorpRow {
  /** Points/week surplus over positional replacement, 1dp, floored at 0 —
   *  the STORED/DISPLAYED figure. */
  vorp: number;
  /** UNFLOORED, unrounded surplus (production − R_pos; negative below
   *  replacement) — the RANKING figure fed to rankVorp, so below-replacement
   *  players order by their own positionally-normalized shortfall. */
  surplus: number;
  /** The lens's production signal: recency-weighted ppg over COMPLETED
   *  seasons, unrounded. 0 ⇒ no completed-season production (rankVorp's
   *  tail band). */
  wppg: number;
  /** max(0, surplus) × contractFactor — the ranking/bones figure. */
  value: number;
}

export interface VorpResult {
  rows: Map<string, VorpRow>;
  demandByPos: Map<string, number>;
  replacementByPos: Map<string, number>;
  /** Lens vintage — the latest completed season with start signal (for the
   *  UI's "through <year>" label); undefined when the lens has no signal. */
  vorpSeason?: number;
}

/** The BDL-VORP pipeline over COMPLETED seasons only: measured demand →
 *  empirical replacement → usage-weighted production → VORP =
 *  max(0, P − R_pos). Pure and deterministic across input orderings.
 *  The replacement pool is every player's completed-season wppg at the
 *  position (zero-producers included — a rookie truthfully supplies 0), and
 *  positions absent from the demand map fall back to demand 0, which still
 *  yields a sane replacement (the (1 + REPLACEMENT_BUFFER)-th player). */
export function computeVorpAll(inputs: VorpInput[], currentLeagueYear: number): VorpResult {
  const completed = inputs.map((r) => ({
    nflId: r.nflId,
    position: r.position,
    contractYears: r.contractYears,
    seasons: completedSeasons(r.seasons, currentLeagueYear),
  }));
  const vorpSeason = selectVorpSeason(completed, currentLeagueYear);
  const demandByPos = measureDemand(completed);

  const prelim = completed.map((r) => ({
    ...r,
    wppg: weightedPpg(r.seasons),
  }));
  // Both legs of the surplus use the same delivered-rate wppg figure — the
  // bar and the jumpers must share a currency (fix 2026-08-27; the earlier
  // usage discount on one leg cut the live QB positive band at 12 of the ~21
  // measured demand implies). The replacement pool is built from these same
  // weightedPpg outputs, so the delivered-rate revision flows into the bar
  // automatically.
  const prodByPos = new Map<string, number[]>();
  for (const r of prelim) {
    const list = prodByPos.get(r.position) ?? [];
    list.push(r.wppg);
    prodByPos.set(r.position, list);
  }
  const replacementByPos = new Map<string, number>();
  for (const [pos, prods] of prodByPos) {
    // No rounding: replacementPpg returns an element of the input list, and
    // the surplus built on it must stay unrounded for ranking (see VorpRow).
    replacementByPos.set(pos, replacementPpg(prods, demandByPos.get(pos) ?? 0));
  }

  const rows = new Map<string, VorpRow>();
  for (const r of prelim) {
    const production = r.wppg;
    const surplus = production - (replacementByPos.get(r.position) ?? 0);
    // Round only for display/storage; ranking consumes the raw surplus.
    rows.set(r.nflId, {
      vorp: round(Math.max(0, surplus), 1),
      surplus,
      wppg: r.wppg,
      value: round(Math.max(0, surplus) * (1 + CONTRACT_FACTOR_PER_YEAR * r.contractYears), 3),
    });
  }
  return { rows, demandByPos, replacementByPos, vorpSeason };
}

/** Deterministic ordering for the VORP lens, mirroring rankAll's mechanics.
 *
 *  Two bands:
 *  - Players WITH BDL production (wppg > 0): UNFLOORED surplus desc (the raw
 *    production − R_pos from computeVorpAll, not the 0-floored stored vorp),
 *    ties wppg desc then nflId asc. Below-replacement players therefore
 *    order by their own positionally-normalized shortfall — a QB 2 pts under
 *    QB replacement ranks above a TE 5 pts under TE replacement — and, by
 *    design, ANY producer (however far below replacement) still ranks above
 *    the no-production tail: a negative surplus is a measurement, no
 *    production is no signal.
 *  - Players with ZERO completed-season production (wppg === 0 — the wppg
 *    here is the LENS wppg from computeVorpAll: KTC-priced rookies who have
 *    never played a completed BDL season, plus true zero-scorers): the lens
 *    has literally no signal on them, so rather than scatter them
 *    nonsensically they form a TAIL ordered by ktcValue desc (market's best
 *    guess), ties nflId asc. Documented rookie rule per the commissioner.
 *  vorpPosRank counts within position along the same ordering. */
export function rankVorp(
  rows: Array<{
    nflId: string;
    position: string;
    /** Contract-scaled lens value (VorpRow.value) — the primary key. */
    value: number;
    surplus: number;
    wppg: number;
    ktcValue?: number;
  }>,
): Array<{ nflId: string; vorpRank: number; vorpPosRank: number }> {
  const byId = (a: { nflId: string }, b: { nflId: string }): number =>
    a.nflId < b.nflId ? -1 : a.nflId > b.nflId ? 1 : 0;
  const sorted = [...rows].sort((a, b) => {
    const aTail = a.wppg === 0 ? 1 : 0;
    const bTail = b.wppg === 0 ? 1 : 0;
    if (aTail !== bTail) return aTail - bTail; // production band first
    if (aTail === 1) {
      const aKtc = a.ktcValue ?? 0;
      const bKtc = b.ktcValue ?? 0;
      if (bKtc !== aKtc) return bKtc - aKtc;
      return byId(a, b);
    }
    // Contract-scaled value first (team control multiplies positive surplus);
    // the zero band then orders by its unfloored positional shortfall.
    if (b.value !== a.value) return b.value - a.value;
    if (b.surplus !== a.surplus) return b.surplus - a.surplus;
    if (b.wppg !== a.wppg) return b.wppg - a.wppg;
    return byId(a, b);
  });
  const posCounters = new Map<string, number>();
  return sorted.map((row, i) => {
    const vorpPosRank = (posCounters.get(row.position) ?? 0) + 1;
    posCounters.set(row.position, vorpPosRank);
    return { nflId: row.nflId, vorpRank: i + 1, vorpPosRank };
  });
}

/** Overall and positional ranks: value desc; ties broken by wppg desc, then
 *  nflId asc, so re-runs over identical data are byte-stable. */
export function rankAll(
  rows: Array<{ nflId: string; position: string; breakdown: ValueBreakdown }>,
): Array<{ nflId: string; rank: number; posRank: number }> {
  const sorted = [...rows].sort((a, b) => {
    if (b.breakdown.value !== a.breakdown.value) return b.breakdown.value - a.breakdown.value;
    if (b.breakdown.wppg !== a.breakdown.wppg) return b.breakdown.wppg - a.breakdown.wppg;
    return a.nflId < b.nflId ? -1 : a.nflId > b.nflId ? 1 : 0;
  });
  const posCounters = new Map<string, number>();
  return sorted.map((row, i) => {
    const posRank = (posCounters.get(row.position) ?? 0) + 1;
    posCounters.set(row.position, posRank);
    return { nflId: row.nflId, rank: i + 1, posRank };
  });
}
